import {EventCatalogueSortField} from "../shared/enums/eventCatalogueSortField";
import {EventTemplateStatus} from "../shared/enums/eventTemplateStatus";
import EventTemplate from "../stores/domainStore/pages/models/eventTemplate";
import {IBackendCaller} from "./backendCaller";
import {EventCalendarType} from "../shared/enums/eventCalendarType";

/**
 * The interface for EventCatalogueService.
 * @interface
 */
export interface IEventCatalogueService {
  /**
   * Set the status for an event template
   */
  setEventTemplateStatus: (
    id: string,
    status: EventTemplateStatus
  ) => Promise<void>;
  /**
   * Gets events templates grouped into categories, with optional search term
   */
  getEventTemplateCategories: (
    status: EventTemplateStatus,
    maxTemplates: number,
    sortField: EventCatalogueSortField,
    searchTerm?: string,
    showDynamicCategories?: boolean,
    calendarType?: EventCalendarType,
    abortController?: AbortController
  ) => Promise<any>;
  /**
   * Gets events templates as a simple list, with optional search term
   */
  getEventTemplates: (
    status: EventTemplateStatus,
    sortField: EventCatalogueSortField,
    searchTerm?: string,
    calendarType?: EventCalendarType,
    abortController?: AbortController
  ) => Promise<any>;
  /**
   * Get an event category by category ID and status.
   */
  getEventTemplateCategory: (
    categoryId: string,
    status: EventTemplateStatus,
    calendarType: EventCalendarType
  ) => Promise<any>;
  /**
   * Create an event template and get the created event template id.
   */
  createEventTemplate: (eventTemplate: EventTemplate) => Promise<any>;
  /**
   * Update an existing event template
   */
  updateEventTemplate: (eventTemplate: EventTemplate) => Promise<any>;
  /**
   * Retrieve an event template by id.
   */
  getEventTemplateById: (id: string) => Promise<any>;
}

/**
 * Provides various calls to event catalogue API.
 */
export default class EventCatalogueService implements IEventCatalogueService {
  private readonly backendCaller: IBackendCaller;
  private readonly eventTemplateCategoriesUrl = "/api/EventTemplateCategories";
  private readonly eventTemplatesUrl = "/api/EventTemplates";

  constructor(backendCaller: IBackendCaller) {
    this.backendCaller = backendCaller;
  }

  setEventTemplateStatus = async (
    id: string,
    status: EventTemplateStatus
  ): Promise<void> => {
    let url = `${this.eventTemplatesUrl}/${id}/statuses/${status}`;
    return this.backendCaller.callApi("POST", url, null);
  };

  getEventTemplateCategories = async (
    status: EventTemplateStatus,
    maxTemplates: number,
    sortField: EventCatalogueSortField,
    searchTerm?: string,
    showDynamicCategories?: boolean,
    calendarType: EventCalendarType = EventCalendarType.Enhanced,
    abortController?: AbortController
  ): Promise<any> => {
    let url = `${this.eventTemplateCategoriesUrl}?status=${status}&maxTemplates=${maxTemplates}&sortField=${sortField}&calendarType=${calendarType}`;
    if (searchTerm) {
      url += `&searchTerm=${searchTerm}`;
    }
    if (showDynamicCategories) {
      url += `&showDynamicCategories=${showDynamicCategories}`;
    }

    return this.backendCaller.callApi(
      "GET",
      url,
      null,
      undefined,
      undefined,
      abortController
    );
  };

  getEventTemplates = async (
    status: EventTemplateStatus,
    sortField: EventCatalogueSortField,
    searchTerm?: string,
    calendarType: EventCalendarType = EventCalendarType.Enhanced,
    abortController?: AbortController
  ): Promise<any> => {
    let url = `${this.eventTemplatesUrl}?status=${status}&sortField=${sortField}&calendarType=${calendarType}`;
    if (searchTerm) {
      url += `&searchTerm=${searchTerm}`;
    }

    return this.backendCaller.callApi(
      "GET",
      url,
      null,
      undefined,
      undefined,
      abortController
    );
  };

  getEventTemplateCategory = async (
    categoryId: string,
    status: EventTemplateStatus,
    calendarType: EventCalendarType = EventCalendarType.Enhanced
  ): Promise<any> => {
    return this.backendCaller.callApi(
      "GET",
      `${this.eventTemplateCategoriesUrl}/${categoryId}?status=${status}&calendarType=${calendarType}`,
      null
    );
  };

  createEventTemplate = async (eventTemplate: EventTemplate): Promise<any> => {
    return this.backendCaller.callApi(
      "POST",
      this.eventTemplatesUrl,
      JSON.stringify(eventTemplate.requestObject)
    );
  };

  updateEventTemplate = async (eventTemplate: EventTemplate): Promise<any> => {
    return this.backendCaller.callApi(
      "PUT",
      `${this.eventTemplatesUrl}/${eventTemplate.id}`,
      JSON.stringify(eventTemplate.requestObject)
    );
  };

  getEventTemplateById(id: string): Promise<any> {
    return this.backendCaller.callApi("GET", `${this.eventTemplatesUrl}/${id}`);
  }
}
